/**
 * Friendly, user-facing copy for entitlement-error codes.
 *
 * The server serializes an `EntitlementError` with the raw code as its
 * `error`/`message` (there is no human string in the payload — see
 * server/lib/entitlement.ts). Without a lookup, a client that renders
 * `result.error` shows the bare constant (e.g. "TRIAL_FAMILY_LIMIT").
 *
 * This map is the single source of truth for turning those codes into copy a
 * person can read. Product voice: "safe" (not "vault"), "family", "safe
 * password". Keep messages short, blame-free, and pointed at the next action.
 *
 * Call `friendlyEntitlementError(code)` — it falls back to a generic message
 * for unknown/internal codes, so a call site can always render its result.
 */

import type { EntitlementErrorCode } from './entitlementError';

/** Shown when we have no specific copy for a code (or the code is unknown). */
export const GENERIC_ERROR_MESSAGE =
  'Something went wrong. Please try again.';

export const ENTITLEMENT_ERROR_MESSAGES: Record<EntitlementErrorCode, string> = {
  // Trial users get one family; a second create needs a subscription.
  TRIAL_FAMILY_LIMIT:
    'You need a subscription to create more than one family. Subscribe to add another.',

  // Creating a family at all is a subscriber feature.
  NO_ENTITLEMENT_CREATE_FAMILY:
    'Creating a family is a subscriber feature. Subscribe to set up your family.',

  // Inviting people into a family is a subscriber feature.
  NO_ENTITLEMENT_INVITE_MEMBER:
    'Inviting members is a subscriber feature. Subscribe to add people to your family.',

  // Access to a family's shared safe requires an active subscription.
  NO_ENTITLEMENT_FAMILY_VAULT_ACCESS:
    "Your subscription doesn't currently include access to this family's safe. Renew your subscription to regain access.",

  // Trusted-contact account recovery is a subscriber feature.
  NO_ENTITLEMENT_RECOVERY:
    'Account recovery is a subscriber feature. Subscribe to set up trusted recovery contacts.',

  // The picked device/surface isn't covered by the current plan.
  SURFACE_NOT_ALLOWED:
    "Your plan doesn't include this device. Update your plan, or switch to a device your plan covers.",

  // No unused subscription available to use here (mostly dead-code post gate fix).
  NO_AVAILABLE_SUBSCRIPTION:
    "You don't have an available subscription to use here. Subscribe to continue.",

  // Email must be verified before this action.
  EMAIL_NOT_VERIFIED:
    'Please verify your email address first — check your inbox for the verification link, then try again.',

  // Switched the active device too many times in a short window.
  SURFACE_SWITCH_RATE_LIMIT:
    "You've switched devices a lot recently. Please wait a little while before switching again.",

  // No Stripe customer on file yet.
  NO_STRIPE_CUSTOMER:
    "We couldn't find a billing account for you yet. Start a subscription to set one up.",

  // Trying to manage a subscription owned by another account.
  NOT_YOUR_SUBSCRIPTION:
    "This subscription belongs to a different account, so it can't be managed here.",

  // Server-to-server (Stripe webhook) — never reaches a user. Generic fallback.
  INVALID_WEBHOOK_SIGNATURE: GENERIC_ERROR_MESSAGE,

  // Every seat in the subscription is in use.
  POOL_FULL:
    "You've used all the seats in your subscription. Add more seats to invite additional people.",
};

/**
 * Friendly copy for an entitlement-error code. Accepts the loose `string`
 * a client gets from `result.code` and falls back to a generic message for
 * anything unrecognized, so callers never render a bare constant.
 */
export function friendlyEntitlementError(code: string | undefined | null): string {
  if (code && code in ENTITLEMENT_ERROR_MESSAGES) {
    return ENTITLEMENT_ERROR_MESSAGES[code as EntitlementErrorCode];
  }
  return GENERIC_ERROR_MESSAGE;
}

/**
 * Resolve display copy for a failed API result: friendly copy when `code` is a
 * known entitlement code, otherwise the server's own `message` (ordinary
 * handler errors already carry a human string). Use this at any call site that
 * would otherwise render `result.error` raw.
 */
export function entitlementErrorOr(
  code: string | undefined | null,
  message: string,
): string {
  if (code && code in ENTITLEMENT_ERROR_MESSAGES) {
    return ENTITLEMENT_ERROR_MESSAGES[code as EntitlementErrorCode];
  }
  return message;
}
